import React from "react";
import styled from "styled-components";
import Page from "../../components/Page";
import Async from "../../components/Async";
import api from "../../services/api";
import { Button } from "../../components/buttons";
import { borderColor, spacing } from "../../components/layout";

const DEFAULT_RECENT_LIMIT = 100;
const RECENT_LIMIT_OPTIONS = [40, 100, 200];

const searchQueryFromLocation = location => {
  const search = (location && location.search) || "";
  if (!search) {
    return "";
  }
  try {
    const params = new URLSearchParams(search);
    return (params.get("query") || "").trim();
  } catch (error) {
    return "";
  }
};

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${spacing};
  margin-bottom: 20px;
`;

const ToolbarText = styled.div`
  max-width: 740px;
  line-height: 1.45;
`;

const ToolbarActions = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const SearchBarRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 14px;
`;

const SearchInput = styled.input`
  min-width: 320px;
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid ${borderColor};
  background: var(--surface-bg);
  color: var(--text-color);
  outline: none;

  &::placeholder {
    color: var(--muted-text);
  }
`;

const LimitSelect = styled.select`
  min-width: 110px;
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid ${borderColor};
  background: var(--surface-bg);
  color: var(--text-color);
  outline: none;
`;

const Muted = styled.div`
  color: var(--muted-text);
  font-size: 13px;
  margin-top: 4px;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${spacing};
  margin-bottom: 24px;
`;

const SummaryCard = styled.div`
  border: 1px solid ${borderColor};
  border-radius: 8px;
  padding: 16px;
  background: var(--surface-bg);
`;

const SummaryValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  margin-top: 6px;
`;

const SummaryLabel = styled.div`
  color: var(--muted-text);
  font-size: 13px;
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: ${spacing};
  margin-bottom: 26px;
`;

const SectionCard = styled.div`
  border: 1px solid ${borderColor};
  border-radius: 8px;
  padding: 16px;
  background: var(--surface-bg);
`;

const SectionTitle = styled.div`
  font-weight: 700;
  margin-bottom: 14px;
`;

const BreakdownRow = styled.div`
  display: grid;
  grid-template-columns: minmax(90px, 1fr) 70px;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;
`;

const BreakdownLabel = styled.div`
  min-width: 0;
`;

const BreakdownLabelText = styled.div`
  font-size: 13px;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BreakdownBar = styled.div`
  width: 100%;
  height: 8px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 999px;
  overflow: hidden;
`;

const BreakdownFill = styled.div`
  height: 100%;
  width: ${props => props.width}%;
  background: #54d2b1;
  border-radius: 999px;
`;

const BreakdownCount = styled.div`
  text-align: right;
  font-weight: 600;
`;

const EmptyState = styled.div`
  color: var(--muted-text);
  font-size: 13px;
`;

const FlowList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing};
`;

const FlowCard = styled.details`
  border: 1px solid ${borderColor};
  border-radius: 10px;
  background: var(--surface-bg);
  overflow: hidden;
`;

const FlowSummary = styled.summary`
  list-style: none;
  cursor: pointer;
  padding: 16px;
  display: flex;
  gap: 14px;
  align-items: flex-start;

  &::-webkit-details-marker {
    display: none;
  }
`;

const UserThumb = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 999px;
  background: #f3f3f3;
  overflow: hidden;
  flex: 0 0 52px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const FlowSummaryBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const FlowTitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 6px;
`;

const FlowTitle = styled.div`
  font-weight: 700;
  font-size: 15px;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 3px 9px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: ${props => props.background || "rgba(0,0,0,0.07)"};
  color: ${props => props.color || "#1d1d1d"};
`;

const SummaryMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  color: var(--muted-text);
  font-size: 13px;
`;

const FlowBody = styled.div`
  border-top: 1px solid ${borderColor};
  padding: 16px;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
`;

const MetaCard = styled.div`
  border: 1px solid ${borderColor};
  border-radius: 8px;
  padding: 12px;
  background: var(--surface-bg);
`;

const MetaLabel = styled.div`
  font-size: 12px;
  color: var(--muted-text);
  margin-bottom: 6px;
`;

const MetaValue = styled.div`
  font-weight: 600;
  line-height: 1.4;
  word-break: break-word;
`;

const LinkedMeetupCard = styled.div`
  border: 1px solid ${borderColor};
  border-radius: 8px;
  padding: 12px;
  background: var(--surface-bg);
`;

const SmallText = styled.div`
  font-size: 12px;
  line-height: 1.45;
  color: var(--muted-text);
  margin-top: 8px;
`;

const QueueList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 12px;
`;

const QueueCard = styled.div`
  border: 1px solid ${borderColor};
  border-radius: 8px;
  padding: 12px;
  background: var(--surface-bg);
`;

const prettyLabel = value => {
  if (!value) {
    return "Unknown";
  }
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, char => char.toUpperCase());
};

const formatDateTime = value => {
  if (!value) {
    return " - ";
  }
  return new Date(value).toLocaleString();
};

const formatCoordinates = location => {
  if (!location) {
    return "No live coordinates";
  }
  const { lat, lng } = location;
  if (typeof lat !== "number" || typeof lng !== "number") {
    return "No live coordinates";
  }
  return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
};

const formatLiveLocation = location => {
  if (!location) {
    return "No live location";
  }
  return location.city || location.display_label || formatCoordinates(location);
};

const formatLiveLocationDetails = location => {
  if (!location) {
    return "No live location";
  }
  const coordinates = formatCoordinates(location);
  if (location.city && coordinates !== "No live coordinates") {
    return `${location.city} | ${coordinates}`;
  }
  return location.city || location.display_label || coordinates;
};

const stateColors = {
  inactive: { background: "rgba(239, 68, 68, 0.14)", color: "#b91c1c" },
  expired: { background: "rgba(100, 116, 139, 0.16)", color: "#475569" },
  expired_with_opportunity: { background: "rgba(14, 165, 233, 0.16)", color: "#0369a1" },
  opportunity_open: { background: "rgba(16, 185, 129, 0.18)", color: "#047857" },
  opportunity_stale: { background: "rgba(245, 158, 11, 0.18)", color: "#92400e" },
  waiting: { background: "rgba(59, 130, 246, 0.16)", color: "#1d4ed8" }
};

const meetupStatusColors = {
  now: { background: "rgba(84, 210, 177, 0.16)", color: "#0a6b53" },
  started: { background: "rgba(59, 130, 246, 0.16)", color: "#1d4ed8" },
  confirmed: { background: "rgba(52, 211, 153, 0.16)", color: "#047857" },
  partial: { background: "rgba(245, 158, 11, 0.18)", color: "#92400e" },
  pending: { background: "rgba(107, 114, 128, 0.16)", color: "#374151" },
  cancelled: { background: "rgba(239, 68, 68, 0.15)", color: "#b91c1c" },
  expired: { background: "rgba(0, 0, 0, 0.08)", color: "#4b5563" },
  empty: { background: "rgba(0, 0, 0, 0.08)", color: "#4b5563" }
};

const queueStateColors = {
  surfaced: { background: "rgba(84, 210, 177, 0.16)", color: "#0a6b53" },
  locked: { background: "rgba(59, 130, 246, 0.16)", color: "#1d4ed8" },
  queued: { background: "rgba(107, 114, 128, 0.16)", color: "#374151" },
  confirmed: { background: "rgba(52, 211, 153, 0.16)", color: "#047857" },
  missed: { background: "rgba(245, 158, 11, 0.18)", color: "#92400e" },
  expired: { background: "rgba(0, 0, 0, 0.08)", color: "#4b5563" },
  dismissed: { background: "rgba(239, 68, 68, 0.15)", color: "#b91c1c" }
};

const responseStateColors = {
  pending: { background: "rgba(107, 114, 128, 0.16)", color: "#374151" },
  approved: { background: "rgba(52, 211, 153, 0.16)", color: "#047857" },
  skipped: { background: "rgba(245, 158, 11, 0.18)", color: "#92400e" },
  cancelled: { background: "rgba(239, 68, 68, 0.15)", color: "#b91c1c" }
};

const intentColors = {
  friends: { background: "rgba(59, 130, 246, 0.16)", color: "#1d4ed8" },
  dates: { background: "rgba(244, 114, 182, 0.18)", color: "#be185d" },
  fun: { background: "rgba(245, 158, 11, 0.18)", color: "#92400e" }
};

const intentStyleFor = value => {
  if (!value) {
    return null;
  }
  return intentColors[String(value).toLowerCase()] || null;
};

const sourceColors = {
  organic: { background: "rgba(59, 130, 246, 0.12)", color: "#1e40af" },
  seeded: { background: "rgba(245, 158, 11, 0.18)", color: "#92400e" }
};

const BreakdownList = ({ title, items }) => {
  const maxCount = items.reduce((max, item) => Math.max(max, item.count), 0);

  return (
    <SectionCard>
      <SectionTitle>{title}</SectionTitle>
      {!items.length ? (
        <EmptyState>No data yet.</EmptyState>
      ) : (
        items.map(item => (
          <BreakdownRow key={`${title}-${item.label}`}>
            <BreakdownLabel>
              <BreakdownLabelText>{prettyLabel(item.label)}</BreakdownLabelText>
              <BreakdownBar>
                <BreakdownFill
                  width={maxCount ? Math.max((item.count / maxCount) * 100, 6) : 0}
                />
              </BreakdownBar>
            </BreakdownLabel>
            <BreakdownCount>{item.count}</BreakdownCount>
          </BreakdownRow>
        ))
      )}
    </SectionCard>
  );
};

const OpportunityMeetup = ({ meetup }) => {
  const meetupStyle = meetupStatusColors[meetup.status] || meetupStatusColors.pending;
  const intentStyle = intentStyleFor(meetup.intent);
  const responseStyle = meetup.my_state ? responseStateColors[meetup.my_state] : null;
  const location = meetup.location || {};

  return (
    <LinkedMeetupCard>
      <FlowTitleRow>
        <FlowTitle>Opportunity Meetup #{meetup.id}</FlowTitle>
        <Pill background={meetupStyle.background} color={meetupStyle.color}>
          {prettyLabel(meetup.status)}
        </Pill>
        {meetup.my_state ? (
          <Pill background={responseStyle && responseStyle.background} color={responseStyle && responseStyle.color}>
            {prettyLabel(meetup.my_state)}
          </Pill>
        ) : null}
        {meetup.intent ? (
          <Pill background={intentStyle && intentStyle.background} color={intentStyle && intentStyle.color}>
            {prettyLabel(meetup.intent)}
          </Pill>
        ) : null}
      </FlowTitleRow>
      <MetaValue>
        {prettyLabel(meetup.activity)} | {prettyLabel(meetup.intent)}
      </MetaValue>
      <SmallText>Created {formatDateTime(meetup.created_at)}</SmallText>
      <SmallText>Meetup {formatDateTime(meetup.meetup_time)}</SmallText>
      <SmallText>
        {location.name || location.route_name || "No location label"}
      </SmallText>
      <SmallText>
        {meetup.approved_active_count || 0}/{meetup.active_participant_count || 0} active approved
      </SmallText>
      <SmallText>
        Surface {formatDateTime(meetup.my_surface_activated_at)} to {formatDateTime(meetup.my_surface_expires_at)}
      </SmallText>
    </LinkedMeetupCard>
  );
};

const QueueEntry = ({ item }) => {
  const queueStyle = queueStateColors[item.queue_state] || queueStateColors.queued;
  const meetupStyle = meetupStatusColors[item.meetup_status] || meetupStatusColors.pending;
  const intentStyle = intentStyleFor(item.intent);
  const responseStyle = item.my_state ? responseStateColors[item.my_state] : null;
  const sourceStyle = item.source ? sourceColors[item.source] : null;

  return (
    <QueueCard>
      <FlowTitleRow>
        <FlowTitle>
          #{item.meetup_id} {prettyLabel(item.activity)}
        </FlowTitle>
        <Pill background={queueStyle.background} color={queueStyle.color}>
          {prettyLabel(item.queue_state)}
        </Pill>
        {item.is_foreground ? (
          <Pill background="rgba(17, 24, 39, 0.12)" color="#111827">
            Foreground
          </Pill>
        ) : null}
        {item.meetup_status ? (
          <Pill background={meetupStyle.background} color={meetupStyle.color}>
            {prettyLabel(item.meetup_status)}
          </Pill>
        ) : null}
        {item.my_state ? (
          <Pill background={responseStyle && responseStyle.background} color={responseStyle && responseStyle.color}>
            {prettyLabel(item.my_state)}
          </Pill>
        ) : null}
        {item.intent ? (
          <Pill background={intentStyle && intentStyle.background} color={intentStyle && intentStyle.color}>
            {prettyLabel(item.intent)}
          </Pill>
        ) : null}
        {item.source ? (
          <Pill background={sourceStyle && sourceStyle.background} color={sourceStyle && sourceStyle.color}>
            {prettyLabel(item.source)}
          </Pill>
        ) : null}
      </FlowTitleRow>

      <MetaGrid>
        <MetaCard>
          <MetaLabel>Counterparty</MetaLabel>
          <MetaValue>{(item.other_participant_names || []).join(", ") || "No active counterparty"}</MetaValue>
        </MetaCard>
        <MetaCard>
          <MetaLabel>Meetup Time</MetaLabel>
          <MetaValue>{formatDateTime(item.meetup_time)}</MetaValue>
        </MetaCard>
        <MetaCard>
          <MetaLabel>Expiry Window</MetaLabel>
          <MetaValue>{formatDateTime(item.effective_expires_at || item.surface_expires_at || item.lock_expires_at)}</MetaValue>
        </MetaCard>
        <MetaCard>
          <MetaLabel>Scores</MetaLabel>
          <MetaValue>
            {item.arbitration_score || item.arbitration_score === 0 ? item.arbitration_score : " - "}
            {" | "}
            {item.urgency_score || item.urgency_score === 0 ? item.urgency_score : " - "}
            {" | "}
            {item.actionability_score || item.actionability_score === 0 ? item.actionability_score : " - "}
          </MetaValue>
        </MetaCard>
      </MetaGrid>

      <SmallText>
        {item.intent ? `${prettyLabel(item.intent)} | ` : ""}
        {item.location_label || "No location label"}
        {item.kind ? ` | ${prettyLabel(item.kind)}` : ""}
        {item.is_actionable ? " | Actionable" : ""}
        {item.source_future_plan_id ? ` | Future plan ${item.source_future_plan_id}` : ""}
      </SmallText>
    </QueueCard>
  );
};

const RecentFlowItem = ({ flow, defaultOpen }) => {
  const user = flow.user || {};
  const stateStyle = stateColors[flow.state] || stateColors.waiting;
  const sourceStyle = sourceColors[flow.source] || sourceColors.organic;
  const queueSummary = flow.queue_summary || {};
  const opportunityQueue = flow.opportunity_queue || [];
  const intentStyle = intentStyleFor(flow.intent);
  const userLiveLocation = flow.user_live_location || null;

  return (
    <FlowCard open={defaultOpen}>
      <FlowSummary>
        <UserThumb>
          {user.profile_thumbnail_url ? (
            <img src={user.profile_thumbnail_url} alt={user.name || "User"} />
          ) : null}
        </UserThumb>
        <FlowSummaryBody>
          <FlowTitleRow>
            <FlowTitle>
              #{flow.id} {user.name || `User ${user.id}`}
            </FlowTitle>
            <Pill background={stateStyle.background} color={stateStyle.color}>
              {prettyLabel(flow.state)}
            </Pill>
            <Pill background={sourceStyle.background} color={sourceStyle.color}>
              {prettyLabel(flow.source)}
            </Pill>
            {flow.ending_soon ? (
              <Pill background="rgba(239, 68, 68, 0.15)" color="#b91c1c">
                Ending Soon
              </Pill>
            ) : null}
            {queueSummary.total_count ? <Pill>{queueSummary.total_count} Queue</Pill> : null}
            {flow.intent ? (
              <Pill background={intentStyle && intentStyle.background} color={intentStyle && intentStyle.color}>
                {prettyLabel(flow.intent)}
              </Pill>
            ) : null}
            {flow.activity ? <Pill>{prettyLabel(flow.activity)}</Pill> : null}
          </FlowTitleRow>
          <SummaryMeta>
            <div>Updated {formatDateTime(flow.updated_at)}</div>
            <div>Until {formatDateTime(flow.available_until)}</div>
            <div>{prettyLabel(flow.logistics)}</div>
            <div>{prettyLabel(flow.selected_when)}</div>
            <div>{formatLiveLocation(userLiveLocation)}</div>
            <div>{flow.duration_minutes ? `${flow.duration_minutes} min` : "No duration"}</div>
          </SummaryMeta>
        </FlowSummaryBody>
      </FlowSummary>

      <FlowBody>
        <MetaGrid>
          <MetaCard>
            <MetaLabel>User</MetaLabel>
            <MetaValue>
              {user.name || `User ${user.id || "-"}`}
              {user.age ? ` | ${user.age}` : ""}
              {user.approved ? " | Approved" : " | Not approved"}
            </MetaValue>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Intent / Activity</MetaLabel>
            <MetaValue>
              {prettyLabel(flow.intent)} | {prettyLabel(flow.activity)}
            </MetaValue>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Source</MetaLabel>
            <MetaValue>{prettyLabel(flow.source)}</MetaValue>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Logistics / When</MetaLabel>
            <MetaValue>
              {prettyLabel(flow.logistics)} | {prettyLabel(flow.selected_when)}
            </MetaValue>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Availability Window</MetaLabel>
            <MetaValue>
              {formatDateTime(flow.created_at)} to {formatDateTime(flow.available_until)}
            </MetaValue>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Latest Live Location</MetaLabel>
            <MetaValue>{formatLiveLocationDetails(userLiveLocation)}</MetaValue>
            <SmallText>
              {userLiveLocation && userLiveLocation.updated_at
                ? `Updated ${formatDateTime(userLiveLocation.updated_at)}`
                : "No live location update timestamp"}
            </SmallText>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Current Opportunity</MetaLabel>
            <MetaValue>
              {flow.active_opportunity_meetup_id ? `Meetup #${flow.active_opportunity_meetup_id}` : "None"}
            </MetaValue>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Opportunity Surface</MetaLabel>
            <MetaValue>
              {formatDateTime(flow.active_opportunity_started_at)} to{" "}
              {formatDateTime(flow.active_opportunity_expires_at)}
            </MetaValue>
          </MetaCard>
          <MetaCard>
            <MetaLabel>Queue Summary</MetaLabel>
            <MetaValue>
              {queueSummary.actionable_count || 0} actionable | {queueSummary.surfaced_count || 0} surfaced
            </MetaValue>
          </MetaCard>
        </MetaGrid>

        <SectionTitle>Current Opportunity</SectionTitle>
        {!flow.opportunity_meetup ? (
          <EmptyState>No current AI meetup linked to this live-now flow snapshot.</EmptyState>
        ) : (
          <OpportunityMeetup meetup={flow.opportunity_meetup} />
        )}

        <SectionTitle>Opportunity Queue</SectionTitle>
        {!opportunityQueue.length ? (
          <EmptyState>No recent queue items for this user.</EmptyState>
        ) : (
          <QueueList>
            {opportunityQueue.map(item => (
              <QueueEntry key={`${flow.id}-${item.meetup_id}`} item={item} />
            ))}
          </QueueList>
        )}
      </FlowBody>
    </FlowCard>
  );
};

class LiveIntentFlow extends React.Component {
  constructor(props) {
    super(props);
    const initialSearchQuery = searchQueryFromLocation(props.location);
    this.state = {
      reload: false,
      lastRefreshedAt: null,
      searchQuery: initialSearchQuery,
      appliedSearchQuery: initialSearchQuery,
      recentLimit: DEFAULT_RECENT_LIMIT
    };
  }

  componentDidUpdate(prevProps) {
    const previousQuery = searchQueryFromLocation(prevProps.location);
    const nextQuery = searchQueryFromLocation(this.props.location);
    if (
      previousQuery !== nextQuery &&
      (nextQuery !== this.state.searchQuery || nextQuery !== this.state.appliedSearchQuery)
    ) {
      this.setState(state => ({
        searchQuery: nextQuery,
        appliedSearchQuery: nextQuery,
        reload: !state.reload
      }));
    }
  }

  loadDashboard = async () => {
    const dashboard = await api.live_intent_flow.dashboard({
      recent_limit: this.state.recentLimit,
      query: this.state.appliedSearchQuery || undefined
    });
    this.setState({ lastRefreshedAt: new Date() });
    return dashboard;
  };

  refresh = () => {
    this.setState(state => ({ reload: !state.reload }));
  };

  onSearchChange = event => {
    this.setState({ searchQuery: event.target.value });
  };

  applySearch = () => {
    this.setState(state => ({
      appliedSearchQuery: state.searchQuery.trim(),
      reload: !state.reload
    }));
  };

  clearSearch = () => {
    this.setState(state => ({
      searchQuery: "",
      appliedSearchQuery: "",
      reload: !state.reload
    }));
  };

  onSearchKeyDown = event => {
    if (event.key === "Enter") {
      this.applySearch();
    }
  };

  onRecentLimitChange = event => {
    const nextLimit = parseInt(event.target.value, 10) || DEFAULT_RECENT_LIMIT;
    this.setState(state => ({
      recentLimit: nextLimit,
      reload: !state.reload
    }));
  };

  renderOverview = overview => {
    const cards = [
      { label: "Live availability rows", value: overview.total_rows },
      { label: "Updated last 24h", value: overview.updated_last_24h },
      { label: "Updated last 7d", value: overview.updated_last_7d },
      { label: "Active now", value: overview.active_now_count },
      { label: "Organic active", value: overview.organic_active_now_count },
      { label: "Seeded active", value: overview.seeded_active_now_count },
      { label: "Waiting", value: overview.waiting_count },
      { label: "Organic waiting", value: overview.organic_waiting_count },
      { label: "Seeded waiting", value: overview.seeded_waiting_count },
      { label: "Open opportunities", value: overview.open_opportunity_count },
      { label: "Organic open", value: overview.organic_open_opportunity_count },
      { label: "Seeded open", value: overview.seeded_open_opportunity_count },
      { label: "Ending soon", value: overview.ending_soon_count },
      { label: "Surfaced meetups", value: overview.distinct_opportunity_meetup_count },
      { label: "Recent users with queue", value: overview.recent_queue_user_count },
      { label: "Recent queue items", value: overview.recent_queue_item_count },
      { label: "Recent foreground", value: overview.recent_foreground_count },
      { label: "Recent locked", value: overview.recent_locked_count },
      { label: "Recent surfaced", value: overview.recent_surfaced_count }
    ];

    return (
      <SummaryGrid>
        {cards.map(card => (
          <SummaryCard key={card.label}>
            <SummaryLabel>{card.label}</SummaryLabel>
            <SummaryValue>{card.value || 0}</SummaryValue>
          </SummaryCard>
        ))}
      </SummaryGrid>
    );
  };

  renderDashboard = dashboard => {
    const breakdowns = dashboard.breakdowns || {};
    const recentFlows = dashboard.recent_flows || [];
    const { appliedSearchQuery, searchQuery, recentLimit } = this.state;

    return (
      <React.Fragment>
        {this.renderOverview(dashboard.overview || {})}

        <SectionGrid>
          <BreakdownList title="Availability State" items={breakdowns.by_state || []} />
          <BreakdownList title="Source" items={breakdowns.by_source || []} />
          <BreakdownList title="Queue State" items={breakdowns.by_queue_state || []} />
          <BreakdownList title="Intent (7d)" items={breakdowns.by_intent || []} />
          <BreakdownList title="Activity (7d)" items={breakdowns.by_activity || []} />
          <BreakdownList title="Logistics (7d)" items={breakdowns.by_logistics || []} />
          <BreakdownList title="When (7d)" items={breakdowns.by_when || []} />
          <BreakdownList title="Update Trend (7d)" items={breakdowns.updated_last_7d || []} />
        </SectionGrid>

        <SectionCard>
          <SectionTitle>Recent Live Availability Rows</SectionTitle>
          <SearchBarRow>
              <SearchInput
                value={searchQuery}
                onChange={this.onSearchChange}
                onKeyDown={this.onSearchKeyDown}
                placeholder="Search by name, id, city, location, or time"
              />
            <LimitSelect value={recentLimit} onChange={this.onRecentLimitChange}>
              {RECENT_LIMIT_OPTIONS.map(option => (
                <option key={option} value={option}>
                  Show {option}
                </option>
              ))}
            </LimitSelect>
            <Button onClick={this.applySearch}>Search</Button>
            <Button onClick={this.clearSearch} disabled={!searchQuery && !appliedSearchQuery}>
              Clear
            </Button>
          </SearchBarRow>
          {!recentFlows.length ? (
            <EmptyState>
              {appliedSearchQuery
                ? `No live availability rows found for "${appliedSearchQuery}".`
                : "No live availability rows found yet."}
            </EmptyState>
          ) : (
            <FlowList>
              {recentFlows.map((flow, index) => (
                <RecentFlowItem
                  key={flow.id}
                  flow={flow}
                  defaultOpen={index < 3 || !!(flow.queue_summary || {}).foreground_meetup_id}
                />
              ))}
            </FlowList>
          )}
        </SectionCard>
      </React.Fragment>
    );
  };

  render() {
    const { reload, lastRefreshedAt, appliedSearchQuery, recentLimit } = this.state;

    return (
      <Page title="Live Availability">
        <Toolbar>
          <ToolbarText>
            Snapshot of `UserLiveSessionPreference` rows currently stored on the backend. This tracks the latest
            live availability state for each user, plus any currently surfaced AI meetup opportunity attached
            to it, plus that user&apos;s current opportunity queue. Source distinguishes real app traffic from
            the seeded dormant pool.
            <Muted>
              {lastRefreshedAt
                ? `Last refreshed ${lastRefreshedAt.toLocaleString()}`
                : "Waiting for first refresh"}
              {appliedSearchQuery ? ` | Search: ${appliedSearchQuery}` : ""}
              {` | Showing ${recentLimit}`}
            </Muted>
          </ToolbarText>
          <ToolbarActions>
            <Button onClick={this.refresh}>Refresh now</Button>
          </ToolbarActions>
        </Toolbar>

        <Async fetchProps={this.loadDashboard} reload={reload}>
          {this.renderDashboard}
        </Async>
      </Page>
    );
  }
}

export default LiveIntentFlow;
